import { parsePhoneNumberFromString } from 'libphonenumber-js';

export const validateAndNormalizePhone = (rawPhone, iso2Lower) => {
  const value = String(rawPhone || '').trim(); // convert to string + remove surrounding spaces
  if (!value) return { ok: false, e164: '', error: 'REQUIRED' }; // empty input -> required error

  const iso2 = (iso2Lower || '').toUpperCase(); // libphonenumber-js expects ISO2 country codes in UPPERCASE (e.g. "de" -> "DE")

  const parsed = parsePhoneNumberFromString(value, iso2 || undefined); // parse the phone number; iso2 is used as the default country if the number is not written in full international format

  if (!parsed) return { ok: false, e164: '', error: 'PARSE_FAILED' }; // could not parse the input at all
  if (!parsed.isValid()) return { ok: false, e164: '', error: 'INVALID' }; // parsed, but not a valid phone number

  return { ok: true, e164: parsed.number, error: '' }; // success: return normalized E.164 format like "+491721234567"
}; // validates a phone number and returns it normalized to E.164 (or an error code if invalid)

export const formatCountryLabel = (names, missingName = '') => {
  const primaryName = names.primary || missingName;
  const specialTerritory = names.specialTerritory || '';
  const supplementary = names.supplementary || '';

  let displayName = primaryName;
  if (specialTerritory && supplementary) {
    displayName = `${primaryName} / ${specialTerritory} ${supplementary}`;
  } else if (specialTerritory) {
    displayName = `${primaryName} / ${specialTerritory}`;
  } else if (supplementary) {
    displayName = `${primaryName} ${supplementary}`;
  }

  return displayName;
}; // the country name shown in the country search and lists, from one language's names: the primary name, then " / special territory" and the supplementary name if the country has them

export const getCountryDisplayName = (country, lang) => {
  if (!country?.names?.[lang]) return '';
  const { primary = '', specialTerritory = '' } = country.names[lang];
  return specialTerritory ? `${primary} / ${specialTerritory}` : primary;
}; // this helper function creates the string of the selected country, that is being saved in the users localStorage

export const getCountryLabelFromId = (
  countries,
  countryId,
  lang,
  fallback = ''
) => {
  if (!countryId) return '';
  const match = countries.find((c) => c._id === countryId);
  return match ? getCountryDisplayName(match, lang) : fallback;
}; // this helper function gets a country out of the countries list, based on it's countryId, and returns the string that is being saved in the users localStorage

export const flattenZoneCountries = (zones, lang) => {
  if (!zones || zones.length === 0) return [];

  return zones
    .flatMap((zone) =>
      zone.countries.map((country) => {
        // Get the primary name and secondary names for filtering.
        const primaryName = country.names?.[lang]?.primary || '';
        const secondaryNames = country.names?.[lang]?.secondary || [];

        return {
          ...country,
          zoneId: zone._id,
          zonePrice: zone.price,
          zoneName: zone.names?.[lang],
          // For filtering/sorting:
          primaryNameLower: primaryName.toLowerCase(),
          secondaryNamesLower: secondaryNames.map((s) => s.toLowerCase()),
        };
      })
    )
    .sort((a, b) => a.primaryNameLower.localeCompare(b.primaryNameLower));
}; // Flatten all countries from zones once zones are loaded

export const filterCountries = (allCountries, searchTerm) => {
  const search = searchTerm.toLowerCase();
  if (!search) {
    return allCountries; // If no input, show all
  }

  const primaryMatches = allCountries.filter((country) =>
    country.primaryNameLower.includes(search)
  ); // Get countries that have a primary name match

  const secondaryMatches = allCountries.filter(
    (country) =>
      !country.primaryNameLower.includes(search) &&
      country.secondaryNamesLower.some((sec) => sec.includes(search))
  ); // Get countries that don't have a primary match but match on secondary names

  return [...primaryMatches, ...secondaryMatches]; // Concatenate primary matches first, then secondary matches, each group in the order of the list, which flattenZoneCountries sorts by the same name (the function sorted both groups again until chain 10, Oct 2026: a no-op on its one caller's list, deleted; user decision)
}; // Filtered list of countries based on user's input in the current language

export const parsePlaceAddress = (addressComponents) => {
  const getComponent = (type) => {
    const component = addressComponents.find((c) => c.types.includes(type));
    return component ? component.long_name : '';
  };

  return {
    street: getComponent('route'),
    houseNumber: getComponent('street_number'),
    city: getComponent('locality'),
    postalCode: getComponent('postal_code'),
  };
}; // the address fields from a place the user picked in the Google Places autocomplete

export const compareGeocodedAddress = (
  { street, houseNumber, city, postalCode, countryCode },
  addressComponents
) => {
  const getComponent = (type, isShortName = false) => {
    const component = addressComponents.find((c) => c.types.includes(type));
    if (!component) return '';
    return isShortName ? component.short_name : component.long_name;
  }; // Helper to get the "long_name" or "short_name"

  const validatedStreet = getComponent('route').toLowerCase();
  const validatedHouseNumber = getComponent('street_number').toLowerCase();
  const validatedCity = getComponent('locality').toLowerCase();
  const validatedPostalCode = getComponent('postal_code').toLowerCase();
  const validatedCountryShort = getComponent('country', true).toLowerCase();

  const isCountryMismatch = countryCode.toLowerCase() !== validatedCountryShort; // Compare to user input

  const validationErrors = {
    street: isCountryMismatch ? true : street.toLowerCase() !== validatedStreet,
    houseNumber: isCountryMismatch
      ? true
      : houseNumber.toLowerCase() !== validatedHouseNumber,
    postalCode: isCountryMismatch
      ? true
      : postalCode.toLowerCase() !== validatedPostalCode,
    city: isCountryMismatch ? true : city.toLowerCase() !== validatedCity,
    country: isCountryMismatch,
  };

  return validationErrors;
}; // compares the address the user entered with the address the Google Geocoding API found for it: true marks a field that doesn't match

export const checkAddressSubmission = async (
  { shipping, billing, billingSameAsShipping, validationBypassed },
  {
    validateAddress,
    storePhones,
    onPhoneError,
    onCountryMissing,
    onApiError,
    onValidation,
    onAddressErrors,
    proceed,
  }
) => {
  const shipCheck = validateAndNormalizePhone(
    shipping.phone,
    shipping.phoneCountry
  ); // validate and normalize the phone number of the shippingAddress

  if (!shipCheck.ok) {
    // if the phone number could not be parsed or is invalid:
    onPhoneError('shipping', shipCheck.error); // display an error message in the form
    return; // don't submit the form
  }

  let billCheck = shipCheck;
  if (!billingSameAsShipping) {
    // if the billingAddress is different from the shippingAddress:
    const res = validateAndNormalizePhone(billing.phone, billing.phoneCountry); // validate and normalize the phone number of the billingAddress

    if (!res.ok) {
      // if the phone number could not be parsed or is invalid:
      onPhoneError('billing', res.error); // display an error message in the form
      return; // don't submit the form
    }

    billCheck = res;
  }

  storePhones({
    shipping: shipCheck.e164,
    billing: billCheck.e164,
  }); // the validated and normalized phone numbers, for the submission

  // make sure selectedShippingCountry is set:
  if (!shipping.country?.countryCode) {
    onCountryMissing('shipping');
    return;
  }

  if (validationBypassed) {
    // if address validation has failed and the user has already been notified to double-check the address, continue with submission:
    proceed();
    return;
  }

  // 1) Validate Shipping Address
  const shippingValidation = await validateAddress({
    street: shipping.street,
    houseNumber: shipping.houseNumber,
    city: shipping.city,
    postalCode: shipping.postalCode,
    countryCode: shipping.country.countryCode,
  });

  // Check if shipping validation returns an error from the API
  if (
    shippingValidation.error === 'QUOTA_EXCEEDED' ||
    shippingValidation.error === 'API_NOT_LOADED' ||
    shippingValidation.error === 'VALIDATION_FAILED'
  ) {
    onApiError(); // show the special modal message
    return; // Do not proceed
  }

  onValidation('shipping', shippingValidation);
  let shippingHasError = Object.values(shippingValidation).some((v) => v);

  // 2) Validate Billing Address only if different
  let billingHasError = false;
  let billingValidation = {};

  if (!billingSameAsShipping) {
    // make sure selectedBillingCountry is set:
    if (!billing.country?.countryCode) {
      onCountryMissing('billing');
      return;
    }

    billingValidation = await validateAddress({
      street: billing.street,
      houseNumber: billing.houseNumber,
      city: billing.city,
      postalCode: billing.postalCode,
      countryCode: billing.country.countryCode,
    });

    // Check for API error in billing validation
    if (
      billingValidation.error === 'QUOTA_EXCEEDED' ||
      billingValidation.error === 'API_NOT_LOADED' ||
      billingValidation.error === 'VALIDATION_FAILED'
    ) {
      onApiError();
      return;
    }

    onValidation('billing', billingValidation);
    billingHasError = Object.values(billingValidation).some((v) => v);
  }

  // 3) If either shipping or billing has an error, show modal
  if (shippingHasError || billingHasError) {
    let errorType = '';
    if (shippingHasError && billingHasError) {
      errorType = 'both';
    } else if (shippingHasError) {
      errorType = 'shipping';
    } else if (billingHasError) {
      errorType = 'billing';
    }
    onAddressErrors(errorType);
    return; // Stop here, do not dispatch or navigate yet
  }

  // 4) If no validation errors, proceed normally
  proceed();
}; // the checks ShippingAddressScreen's submit runs, in order: the phone numbers (the billing one when the addresses differ), the shipping country, the bypass after a failed validation, the shipping and then the billing address against the geocoder (an unusable answer stops with the API message), then the fields the geocoder didn't match; each outcome reaches the screen through a callback (PR 32)

export const addressesToSave = ({
  shipping,
  billing,
  billingSameAsShipping,
  phones,
  allCountries,
  selectedLanguage,
}) => {
  const shippingCountryIdFinal =
    shipping.countryId || shipping.selectedCountry?._id || ''; // final shipping country _id (prefer saved value, otherwise use currently selected country, otherwise empty)
  const billingCountryIdFinal = billingSameAsShipping
    ? shippingCountryIdFinal // if billing is the same as shipping, reuse the shipping country _id
    : billing.countryId || billing.selectedCountry?._id || ''; // otherwise: final billing country _id (prefer saved value, otherwise use currently selected country, otherwise empty)

  const shippingCountryLabel = getCountryLabelFromId(
    allCountries,
    shippingCountryIdFinal,
    selectedLanguage,
    shipping.country
  ); // re-compute the string for the selected shipping country, that is being saved in the users localStorage (instead of just using countryShipping directly). We do this, because the user might have changed the selectedLanguage and countryShipping (which we initially get from the users localStorage) might still be the country in the old selected language.
  const billingCountryLabel = billingSameAsShipping
    ? shippingCountryLabel // unread: the same-address branch below copies the shipping address (left as is, user decision, Oct 2026)
    : getCountryLabelFromId(
        allCountries,
        billingCountryIdFinal,
        selectedLanguage,
        billing.country
      ); // re-compute the string for the selected billing country, that is being saved in the users localStorage (instead of just using countryBilling directly). We do this, because the user might have changed the selectedLanguage and countryShipping (which we initially get from the users localStorage) might still be the country in the old selected language.

  const shippingAddress = {
    fullName: shipping.fullName,
    companyName: shipping.companyName,
    street: shipping.street,
    houseNumber: shipping.houseNumber,
    city: shipping.city,
    postalCode: shipping.postalCode,
    country: shippingCountryLabel,
    countryId: shippingCountryIdFinal,
    phone: phones.shipping,
    email: shipping.email,
  };

  const billingAddress = billingSameAsShipping
    ? { ...shippingAddress }
    : {
        fullName: billing.fullName,
        companyName: billing.companyName,
        street: billing.street,
        houseNumber: billing.houseNumber,
        city: billing.city,
        postalCode: billing.postalCode,
        country: billingCountryLabel,
        countryId: billingCountryIdFinal,
        phone: phones.billing,
        email: billing.email,
      };

  return { shippingAddress, billingAddress };
}; // the addresses the checkout stores once the checks passed: the country's label in the selected language from its id (the typed label when the id finds no country), the normalized phones, and the shipping address copied as the billing one when they are the same (ShippingAddressScreen's proceedSubmission, PR 32)
